import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, RotateCcw, AlertTriangle, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { formatCurrency } from "@/components/Common";
import api, { formatApiError } from "@/lib/api";

function GroupPreview({ info, L }) {
  return (
    <div className="rounded-lg border border-white/10 bg-navy p-3 text-sm space-y-1.5" data-testid="atx-del-preview">
      {info.entries.map((x) => (
        <div key={x.id} className="flex justify-between gap-3">
          <span className="text-slate-400 truncate">{x.entry_type}{x.reversal_of ? ` · ${L("storno", "reversal", "Storno")}` : ""}</span>
          <span className={`font-mono ${x.direction === "credit" ? "text-emerald-400" : x.direction === "debit" ? "text-red-400" : "text-slate-500"}`}>
            {x.direction === "credit" ? "+" : x.direction === "debit" ? "−" : ""}{formatCurrency(x.amount, x.currency)}
          </span>
        </div>
      ))}
      <div className="flex justify-between border-t border-white/10 pt-1.5"><span className="text-slate-400">{L("Saldo disponibile del cliente", "Client available balance", "Verfügbarer Kundensaldo")}</span><span className="font-mono text-slate-200" data-testid="atx-del-balance-current">{formatCurrency(info.balance_current, info.currency)}</span></div>
      <p className="text-xs text-emerald-400" data-testid="atx-del-balance-unchanged">{L("Il saldo resta invariato: la transazione viene solo nascosta.", "The balance stays unchanged: the transaction is only hidden.", "Der Saldo bleibt unverändert: Die Transaktion wird nur ausgeblendet.")}</p>
    </div>
  );
}

function LinkedNotice({ info, L }) {
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200 space-y-1" data-testid="atx-del-links">
      <p className="flex items-center gap-1.5 font-medium"><Link2 className="h-3.5 w-3.5" />{L("Elementi collegati", "Linked items", "Verknüpfte Elemente")}</p>
      {info.withdrawal && <p>{L("Prelievo", "Withdrawal", "Auszahlung")} {formatCurrency(info.withdrawal.amount, info.withdrawal.currency)} · {info.withdrawal.status}</p>}
      {info.deposit && <p>{L("Deposito", "Deposit", "Einzahlung")} {info.deposit.amount} {info.deposit.asset || ""} · {info.deposit.status}</p>}
      {info.reversal_ids.length > 0 && <p>{L("Storno collegato", "Linked reversal", "Verknüpftes Storno")} ({info.reversal_ids.length})</p>}
      {info.entries.length > 1 && <p>{info.entries.length} {L("movimenti nel libro mastro", "ledger lines", "Hauptbuchzeilen")}</p>}
    </div>
  );
}

export function DeleteTransaction({ entry, isSuper, onDone, L }) {
  const alreadyDeleted = !!entry.deleted;
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [reason, setReason] = useState("");
  const [includeLinked, setIncludeLinked] = useState(false);
  const [permanent, setPermanent] = useState(alreadyDeleted);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setInfo(null); setReason(""); setIncludeLinked(false); setPermanent(alreadyDeleted); return; }
    api.get(`/admin/transactions/${entry.id}/delete-preview`).then(({ data }) => setInfo(data)).catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  }, [open, entry.id, alreadyDeleted]);

  const needsLinks = info && !alreadyDeleted && info.has_links;
  const blocked = !info || !reason.trim() || busy || (needsLinks && !includeLinked);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/transactions/${entry.id}/delete`, { reason, permanent, include_linked: includeLinked });
      toast.success(permanent ? L("Transazione eliminata definitivamente", "Transaction permanently deleted", "Transaktion endgültig gelöscht") : L("Transazione eliminata", "Transaction deleted", "Transaktion gelöscht"));
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 h-8" data-testid={`atx-delete-${entry.id}`}>
          <Trash2 className="h-3.5 w-3.5 mr-1" />{alreadyDeleted ? L("Elimina definitivamente", "Delete permanently", "Endgültig löschen") : L("Elimina", "Delete", "Löschen")}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-navy-paper border-white/10 max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{alreadyDeleted ? L("Eliminazione definitiva", "Permanent deletion", "Endgültige Löschung") : L("Elimina transazione", "Delete transaction", "Transaktion löschen")}</DialogTitle>
          <DialogDescription className="text-slate-400">
            {L("La transazione sparirà dalla lista admin e da tutte le sezioni del cliente. Il saldo del cliente non cambia. Il cliente non riceverà alcuna notifica. Resta un registro audit interno.", "The transaction will disappear from the admin list and every client section. The client's balance does not change. The client receives no notification. An internal audit record is kept.", "Die Transaktion verschwindet aus der Admin-Liste und allen Kundenbereichen. Der Kundensaldo ändert sich nicht. Der Kunde erhält keine Benachrichtigung. Ein interner Audit-Eintrag bleibt erhalten.")}
          </DialogDescription>
        </DialogHeader>
        {!info ? <p className="text-sm text-slate-500">{L("Caricamento…", "Loading…", "Wird geladen…")}</p> : (
          <div className="space-y-3">
            <GroupPreview info={info} L={L} />
            {needsLinks && <LinkedNotice info={info} L={L} />}
            {needsLinks && (
              <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer">
                <Checkbox checked={includeLinked} onCheckedChange={(v) => setIncludeLinked(!!v)} data-testid="atx-del-include-linked" />
                <span>{L("Elimina anche gli elementi collegati (altrimenti l'eliminazione è bloccata)", "Also delete linked items (otherwise deletion is blocked)", "Verknüpfte Elemente ebenfalls löschen (sonst ist die Löschung blockiert)")}</span>
              </label>
            )}
            {isSuper && !alreadyDeleted && (
              <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer">
                <Checkbox checked={permanent} onCheckedChange={(v) => setPermanent(!!v)} data-testid="atx-del-permanent" />
                <span>{L("Elimina definitivamente (non ripristinabile, il saldo resta invariato)", "Delete permanently (not restorable, balance stays unchanged)", "Endgültig löschen (nicht wiederherstellbar, Saldo bleibt unverändert)")}</span>
              </label>
            )}
            {permanent && <p className="text-xs text-red-400 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />{L("Operazione irreversibile.", "This cannot be undone.", "Dieser Vorgang ist unwiderruflich.")}</p>}
            <div>
              <label className="text-xs text-slate-400">{L("Motivo interno dell'eliminazione (obbligatorio, non visibile al cliente)", "Internal deletion reason (required, not visible to client)", "Interner Löschgrund (erforderlich, für Kunden nicht sichtbar)")}</label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="bg-navy border-white/10" data-testid="atx-del-reason" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="border-white/20" onClick={() => setOpen(false)} data-testid="atx-del-cancel">{L("Annulla", "Cancel", "Abbrechen")}</Button>
          <Button onClick={submit} disabled={blocked} className="bg-red-600 hover:bg-red-500 text-white" data-testid="atx-del-confirm">
            {busy ? L("Attendere…", "Please wait…", "Bitte warten…") : L("Conferma eliminazione", "Confirm deletion", "Löschung bestätigen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RestoreButton({ entry, onDone, L }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/transactions/${entry.id}/restore`, { reason });
      toast.success(L("Transazione ripristinata", "Transaction restored", "Transaktion wiederhergestellt"));
      setOpen(false); setReason("");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setReason(""); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-400 h-8" data-testid={`atx-restore-${entry.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />{L("Ripristina", "Restore", "Wiederherstellen")}</Button>
      </DialogTrigger>
      <DialogContent className="bg-navy-paper border-white/10">
        <DialogHeader>
          <DialogTitle>{L("Ripristina transazione", "Restore transaction", "Transaktion wiederherstellen")}</DialogTitle>
          <DialogDescription className="text-slate-400">{L("La transazione e gli elementi eliminati con essa torneranno visibili al cliente. Il saldo non cambia.", "The transaction and items deleted with it become visible to the client again. The balance does not change.", "Die Transaktion und die mit ihr gelöschten Elemente werden für den Kunden wieder sichtbar. Der Saldo ändert sich nicht.")}</DialogDescription>
        </DialogHeader>
        <div>
          <label className="text-xs text-slate-400">{L("Motivo del ripristino (obbligatorio)", "Restore reason (required)", "Grund der Wiederherstellung (erforderlich)")}</label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="bg-navy border-white/10" data-testid="atx-restore-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-white/20" onClick={() => setOpen(false)} data-testid="atx-restore-cancel">{L("Annulla", "Cancel", "Abbrechen")}</Button>
          <Button onClick={submit} disabled={!reason.trim() || busy} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="atx-restore-confirm">{busy ? L("Attendere…", "Please wait…", "Bitte warten…") : L("Conferma ripristino", "Confirm restore", "Wiederherstellung bestätigen")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
